// src/services/DocumentSetService.js
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { getRandomColor } from '../utils/documentSetColors';

/**
 * Service for managing document sets
 * 
 * Document sets are stored at the case level and referenced by files
 */
const DocumentSetService = {
  /**
   * Create a new document set for a case
   * @param {string} caseId - Case ID
   * @param {string} name - Set name
   * @param {object} color - Color object (optional - will generate random if not provided)
   * @returns {Promise<object>} - The created set object
   */
  async createDocumentSet(caseId, name, color = null) {
    if (!caseId || !name?.trim()) {
      throw new Error('Case ID and set name are required');
    }

    try {
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);

      if (!caseSnap.exists()) {
        throw new Error('Case not found');
      }

      const caseData = caseSnap.data();
      const existingSets = caseData.documentSets || [];

      // Check for duplicate name
      const duplicate = existingSets.find(
        set => set.name.toLowerCase() === name.trim().toLowerCase()
      );
      if (duplicate) {
        throw new Error('A set with this name already exists');
      }

      // Create new set with provided color or random color
      const newSet = {
        id: `set_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        name: name.trim(),
        color: color || getRandomColor(existingSets),
        order: existingSets.length,
        createdAt: new Date(),
        lastModified: new Date(),
        archived: false, // Sets start as not archived
      };

      // Add to case
      await updateDoc(caseRef, {
        documentSets: arrayUnion(newSet),
      });

      console.log('Created document set:', newSet);
      return newSet;
    } catch (error) {
      console.error('Error creating document set:', error);
      throw error;
    }
  },

  /**
   * Get all document sets for a case
   * @param {string} caseId - Case ID
   * @returns {Promise<array>} - Array of document sets
   */
  async getDocumentSets(caseId) {
    if (!caseId) {
      throw new Error('Case ID is required');
    }

    try {
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);

      if (!caseSnap.exists()) {
        return [];
      }

      const caseData = caseSnap.data();
      return caseData.documentSets || [];
    } catch (error) {
      console.error('Error getting document sets:', error);
      throw error;
    }
  },

  /**
   * Add a document to a set
   * @param {string} caseId - Case ID
   * @param {string} fileId - File ID
   * @param {string} setId - Set ID to add
   */
  async addDocumentToSet(caseId, fileId, setId) {
    if (!caseId || !fileId || !setId) {
      throw new Error('Case ID, file ID, and set ID are required');
    }

    try {
      const fileRef = doc(db, 'cases', caseId, 'files', fileId);
      const fileSnap = await getDoc(fileRef);

      if (!fileSnap.exists()) {
        throw new Error('File not found');
      }

      // Check if the set is archived
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);
      if (caseSnap.exists()) {
        const caseData = caseSnap.data();
        const sets = caseData.documentSets || [];
        const set = sets.find(s => s.id === setId);
        if (set?.archived) {
          throw new Error('Cannot add documents to an archived set');
        }
      }

      const fileData = fileSnap.data();
      const currentSets = fileData.documentSets || [];
      const currentOrder = fileData.documentSetOrder || currentSets;

      // Don't add if already exists
      if (currentSets.includes(setId)) {
        console.log('Document already in set');
        return;
      }

      // Add to sets array
      const newSets = [...currentSets, setId];
      
      // Add to order at the beginning (most recent first)
      const newOrder = [setId, ...currentOrder];

      await updateDoc(fileRef, {
        documentSets: newSets,
        documentSetOrder: newOrder,
      });

      // Update lastModified for the set
      const caseData = caseSnap.data();
      const updatedSets = (caseData.documentSets || []).map(set =>
        set.id === setId ? { ...set, lastModified: new Date() } : set
      );
      await updateDoc(caseRef, {
        documentSets: updatedSets,
      });

      console.log(`Added document ${fileId} to set ${setId}`);
    } catch (error) {
      console.error('Error adding document to set:', error);
      throw error;
    }
  },

  /**
   * Remove a document from a set
   * @param {string} caseId - Case ID
   * @param {string} fileId - File ID
   * @param {string} setId - Set ID to remove
   */
  async removeDocumentFromSet(caseId, fileId, setId) {
    if (!caseId || !fileId || !setId) {
      throw new Error('Case ID, file ID, and set ID are required');
    }

    try {
      // Check if the set is archived
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);

      let caseData = null;
      if (caseSnap.exists()) {
        caseData = caseSnap.data();
        const sets = caseData.documentSets || [];
        const set = sets.find(s => s.id === setId);
        if (set?.archived) {
          throw new Error('Cannot remove documents from an archived set');
        }
      }

      const fileRef = doc(db, 'cases', caseId, 'files', fileId);
      const fileSnap = await getDoc(fileRef);

      if (!fileSnap.exists()) {
        throw new Error('File not found');
      }

      const fileData = fileSnap.data();
      const currentSets = fileData.documentSets || [];
      const currentOrder = fileData.documentSetOrder || currentSets;

      // Remove from both arrays
      const newSets = currentSets.filter(id => id !== setId);
      const newOrder = currentOrder.filter(id => id !== setId);

      await updateDoc(fileRef, {
        documentSets: newSets,
        documentSetOrder: newOrder,
      });

      // Update lastModified for the set
      if (caseData) {
        const updatedSets = (caseData.documentSets || []).map(set =>
          set.id === setId ? { ...set, lastModified: new Date() } : set
        );
        await updateDoc(caseRef, {
          documentSets: updatedSets,
        });
      }

      console.log(`Removed document ${fileId} from set ${setId}`);
    } catch (error) {
      console.error('Error removing document from set:', error);
      throw error;
    }
  },

  /**
   * Clear all sets from a document
   * @param {string} caseId - Case ID
   * @param {string} fileId - File ID
   */
  async clearAllSetsFromDocument(caseId, fileId) {
    if (!caseId || !fileId) {
      throw new Error('Case ID and file ID are required');
    }

    try {
      // Check if any sets are archived
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);
      const fileRef = doc(db, 'cases', caseId, 'files', fileId);
      const fileSnap = await getDoc(fileRef);

      if (caseSnap.exists() && fileSnap.exists()) {
        const caseData = caseSnap.data();
        const fileData = fileSnap.data();
        const sets = caseData.documentSets || [];
        const currentSets = fileData.documentSets || [];

        // Check if any of the file's current sets are archived
        const archivedSets = currentSets.filter(setId => {
          const set = sets.find(s => s.id === setId);
          return set?.archived;
        });

        if (archivedSets.length > 0) {
          throw new Error('Cannot clear all sets: some sets are archived');
        }
      }
      
      await updateDoc(fileRef, {
        documentSets: [],
        documentSetOrder: [],
      });

      console.log(`Cleared all sets from document ${fileId}`);
    } catch (error) {
      console.error('Error clearing sets from document:', error);
      throw error;
    }
  },

  /**
   * Update the custom order of sets for a document
   * @param {string} caseId - Case ID
   * @param {string} fileId - File ID
   * @param {array} newOrder - Array of set IDs in new order
   */
  async updateDocumentSetOrder(caseId, fileId, newOrder) {
    if (!caseId || !fileId || !Array.isArray(newOrder)) {
      throw new Error('Case ID, file ID, and order array are required');
    }

    try {
      const fileRef = doc(db, 'cases', caseId, 'files', fileId);
      
      await updateDoc(fileRef, {
        documentSetOrder: newOrder,
      });

      console.log(`Updated set order for document ${fileId}:`, newOrder);
    } catch (error) {
      console.error('Error updating document set order:', error);
      throw error;
    }
  },

  /**
   * Reorder all document sets at the case level
   * @param {string} caseId - Case ID
   * @param {array} newSetsOrder - Array of set objects in new order
   */
  async reorderAllDocumentSets(caseId, newSetsOrder) {
    if (!caseId || !Array.isArray(newSetsOrder)) {
      throw new Error('Case ID and sets order array are required');
    }

    try {
      const caseRef = doc(db, 'cases', caseId);
      
      // Update the order field for each set
      const updatedSets = newSetsOrder.map((set, index) => ({
        ...set,
        order: index
      }));

      await updateDoc(caseRef, {
        documentSets: updatedSets,
      });

      console.log(`Reordered ${updatedSets.length} document sets`);
    } catch (error) {
      console.error('Error reordering document sets:', error);
      throw error;
    }
  },

  /**
   * Delete a document set from a case
   * Also removes it from all documents
   * @param {string} caseId - Case ID
   * @param {string} setId - Set ID to delete
   */
  async deleteDocumentSet(caseId, setId) {
    if (!caseId || !setId) {
      throw new Error('Case ID and set ID are required');
    }

    try {
      // Get case
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);

      if (!caseSnap.exists()) {
        throw new Error('Case not found');
      }

      const caseData = caseSnap.data();
      const existingSets = caseData.documentSets || [];

      // Check if set is archived
      const set = existingSets.find(s => s.id === setId);
      if (set?.archived) {
        throw new Error('Cannot delete an archived set');
      }

      // Remove the set
      const newSets = existingSets.filter(set => set.id !== setId);

      await updateDoc(caseRef, {
        documentSets: newSets,
      });

      console.log(`Deleted document set ${setId}`);
      
      // Note: We should also remove this set ID from all files
      // This would require a batch operation on all files in the case
      // For now, we'll handle cleanup lazily (ignore missing sets when displaying)
      
    } catch (error) {
      console.error('Error deleting document set:', error);
      throw error;
    }
  },

  /**
   * Rename a document set
   * @param {string} caseId - Case ID
   * @param {string} setId - Set ID to rename
   * @param {string} newName - New name
   */
  async renameDocumentSet(caseId, setId, newName) {
    if (!caseId || !setId || !newName?.trim()) {
      throw new Error('Case ID, set ID, and new name are required');
    }

    try {
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);

      if (!caseSnap.exists()) {
        throw new Error('Case not found');
      }

      const caseData = caseSnap.data();
      const existingSets = caseData.documentSets || [];

      // Find and update the set
      const updatedSets = existingSets.map(set =>
        set.id === setId
          ? { ...set, name: newName.trim(), lastModified: new Date() }
          : set
      );

      await updateDoc(caseRef, {
        documentSets: updatedSets,
      });

      console.log(`Renamed document set ${setId} to "${newName}"`);
    } catch (error) {
      console.error('Error renaming document set:', error);
      throw error;
    }
  },

  /**
   * Toggle lock status of a document set
   * @param {string} caseId - Case ID
   * @param {string} setId - Set ID to toggle lock
   * @returns {Promise<boolean>} - New locked state
   */
  async toggleLockDocumentSet(caseId, setId) {
    if (!caseId || !setId) {
      throw new Error('Case ID and set ID are required');
    }

    try {
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);

      if (!caseSnap.exists()) {
        throw new Error('Case not found');
      }

      const caseData = caseSnap.data();
      const existingSets = caseData.documentSets || [];

      // Find the set and toggle its locked status
      const updatedSets = existingSets.map(set => {
        if (set.id === setId) {
          const newLockedState = !set.locked;
          return { ...set, locked: newLockedState };
        }
        return set;
      });

      await updateDoc(caseRef, {
        documentSets: updatedSets,
      });

      const set = updatedSets.find(s => s.id === setId);
      console.log(`Toggled lock for document set ${setId}: ${set.locked ? 'locked' : 'unlocked'}`);
      return set.locked;
    } catch (error) {
      console.error('Error toggling lock for document set:', error);
      throw error;
    }
  },

  /**
   * Toggle archive status of a document set
   * @param {string} caseId - Case ID
   * @param {string} setId - Set ID to toggle archive
   * @returns {Promise<boolean>} - New archived state
   */
  async toggleArchiveDocumentSet(caseId, setId) {
    if (!caseId || !setId) {
      throw new Error('Case ID and set ID are required');
    }

    try {
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);

      if (!caseSnap.exists()) {
        throw new Error('Case not found');
      }

      const caseData = caseSnap.data();
      const existingSets = caseData.documentSets || [];

      // Find the set and toggle its archived status
      const updatedSets = existingSets.map(set => {
        if (set.id === setId) {
          const newArchivedState = !set.archived;
          return { ...set, archived: newArchivedState };
        }
        return set;
      });

      await updateDoc(caseRef, {
        documentSets: updatedSets,
      });

      const set = updatedSets.find(s => s.id === setId);
      console.log(`Toggled archive for document set ${setId}: ${set.archived ? 'archived' : 'unarchived'}`);
      return set.archived;
    } catch (error) {
      console.error('Error toggling archive for document set:', error);
      throw error;
    }
  },

  /**
   * Batch add multiple sets to multiple documents
   * OPTIMIZED: Uses Firestore writeBatch for single network request on writes
   * @param {string} caseId - Case ID
   * @param {Array<string>} fileIds - Array of file IDs
   * @param {Array<string>} setIds - Array of set IDs to add
   */
  async batchAddSetsToDocuments(caseId, fileIds, setIds) {
    if (!caseId || !fileIds?.length || !setIds?.length) {
      throw new Error('Case ID, file IDs, and set IDs are required');
    }

    try {
      console.log(`Batch adding sets [${setIds.join(', ')}] to ${fileIds.length} documents`);
      const startTime = performance.now();
      
      // Check if any sets are archived
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);
      if (caseSnap.exists()) {
        const caseData = caseSnap.data();
        const sets = caseData.documentSets || [];
        const archivedSets = setIds.filter(setId => {
          const set = sets.find(s => s.id === setId);
          return set?.archived;
        });

        if (archivedSets.length > 0) {
          throw new Error(`Cannot add documents to archived sets: ${archivedSets.join(', ')}`);
        }
      }

      // Phase 1: Parallel reads - fetch all documents
      const readPromises = fileIds.map(fileId => 
        getDoc(doc(db, 'cases', caseId, 'files', fileId))
      );
      const snapshots = await Promise.all(readPromises);
      
      const readTime = performance.now();
      console.log(`📖 Read ${fileIds.length} documents in ${(readTime - startTime).toFixed(0)}ms`);

      // Phase 2: Calculate updates for each document
      const updates = [];
      snapshots.forEach((fileSnap, index) => {
        if (!fileSnap.exists()) {
          console.warn(`File ${fileIds[index]} not found, skipping`);
          return;
        }

        const fileData = fileSnap.data();
        const currentSets = fileData.documentSets || [];
        const currentOrder = fileData.documentSetOrder || currentSets;

        // Calculate union: existing sets + new sets (no duplicates)
        const newSetsToAdd = setIds.filter(setId => !currentSets.includes(setId));
        
        if (newSetsToAdd.length === 0) {
          console.log(`Document ${fileIds[index]} already has all sets, skipping`);
          return;
        }

        const finalSets = [...currentSets, ...newSetsToAdd];
        const finalOrder = [...newSetsToAdd, ...currentOrder]; // New sets at beginning

        updates.push({
          ref: fileSnap.ref,
          data: {
            documentSets: finalSets,
            documentSetOrder: finalOrder,
          }
        });
      });

      if (updates.length === 0) {
        console.log('No documents need updating');
        return;
      }

      // Phase 3: Batched writes (chunked by 500 for Firestore limit)
      const BATCH_LIMIT = 500;
      for (let i = 0; i < updates.length; i += BATCH_LIMIT) {
        const chunk = updates.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        
        chunk.forEach(({ ref, data }) => {
          batch.update(ref, data);
        });
        
        await batch.commit();
        console.log(`✍️ Committed batch ${Math.floor(i / BATCH_LIMIT) + 1} (${chunk.length} documents)`);
      }

      const totalTime = performance.now();
      console.log(`✅ Batch add complete: ${setIds.length} sets to ${updates.length} documents in ${(totalTime - startTime).toFixed(0)}ms`);
    } catch (error) {
      console.error('Error in batch add sets:', error);
      throw error;
    }
  },

  /**
   * Batch remove multiple sets from multiple documents
   * OPTIMIZED: Uses Firestore writeBatch for single network request on writes
   * @param {string} caseId - Case ID
   * @param {Array<string>} fileIds - Array of file IDs
   * @param {Array<string>} setIds - Array of set IDs to remove
   */
  async batchRemoveSetsFromDocuments(caseId, fileIds, setIds) {
    if (!caseId || !fileIds?.length || !setIds?.length) {
      throw new Error('Case ID, file IDs, and set IDs are required');
    }

    try {
      console.log(`Batch removing sets [${setIds.join(', ')}] from ${fileIds.length} documents`);
      const startTime = performance.now();
      
      // Check if any sets are archived
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);
      if (caseSnap.exists()) {
        const caseData = caseSnap.data();
        const sets = caseData.documentSets || [];
        const archivedSets = setIds.filter(setId => {
          const set = sets.find(s => s.id === setId);
          return set?.archived;
        });

        if (archivedSets.length > 0) {
          throw new Error(`Cannot remove documents from archived sets: ${archivedSets.join(', ')}`);
        }
      }

      // Phase 1: Parallel reads - fetch all documents
      const readPromises = fileIds.map(fileId => 
        getDoc(doc(db, 'cases', caseId, 'files', fileId))
      );
      const snapshots = await Promise.all(readPromises);
      
      const readTime = performance.now();
      console.log(`📖 Read ${fileIds.length} documents in ${(readTime - startTime).toFixed(0)}ms`);

      // Phase 2: Calculate updates for each document
      const updates = [];
      snapshots.forEach((fileSnap, index) => {
        if (!fileSnap.exists()) {
          console.warn(`File ${fileIds[index]} not found, skipping`);
          return;
        }

        const fileData = fileSnap.data();
        const currentSets = fileData.documentSets || [];
        const currentOrder = fileData.documentSetOrder || currentSets;

        // Calculate difference: remove specified sets
        const finalSets = currentSets.filter(id => !setIds.includes(id));
        const finalOrder = currentOrder.filter(id => !setIds.includes(id));

        // Only update if something changed
        if (finalSets.length === currentSets.length) {
          console.log(`Document ${fileIds[index]} doesn't have any of these sets, skipping`);
          return;
        }

        updates.push({
          ref: fileSnap.ref,
          data: {
            documentSets: finalSets,
            documentSetOrder: finalOrder,
          }
        });
      });

      if (updates.length === 0) {
        console.log('No documents need updating');
        return;
      }

      // Phase 3: Batched writes (chunked by 500 for Firestore limit)
      const BATCH_LIMIT = 500;
      for (let i = 0; i < updates.length; i += BATCH_LIMIT) {
        const chunk = updates.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        
        chunk.forEach(({ ref, data }) => {
          batch.update(ref, data);
        });
        
        await batch.commit();
        console.log(`✍️ Committed batch ${Math.floor(i / BATCH_LIMIT) + 1} (${chunk.length} documents)`);
      }

      const totalTime = performance.now();
      console.log(`✅ Batch remove complete: ${setIds.length} sets from ${updates.length} documents in ${(totalTime - startTime).toFixed(0)}ms`);
    } catch (error) {
      console.error('Error in batch remove sets:', error);
      throw error;
    }
  },

  /**
   * Update the complete set of document sets for a file
   * This replaces the old add/remove pattern with a single idempotent operation
   * @param {string} caseId - Case ID
   * @param {string} fileId - File ID
   * @param {Array<string>} setIds - Complete array of set IDs that should be on this document
   */
  async updateDocumentSets(caseId, fileId, setIds) {
    if (!caseId || !fileId || !Array.isArray(setIds)) {
      throw new Error('Case ID, file ID, and set IDs array are required');
    }

    try {
      const fileRef = doc(db, 'cases', caseId, 'files', fileId);
      const fileSnap = await getDoc(fileRef);

      if (!fileSnap.exists()) {
        throw new Error('File not found');
      }

      // Check if any sets being added are archived
      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);
      if (caseSnap.exists()) {
        const caseData = caseSnap.data();
        const sets = caseData.documentSets || [];
        const fileData = fileSnap.data();
        const currentSets = fileData.documentSets || [];

        // Find sets being added (not currently on document)
        const setsBeingAdded = setIds.filter(id => !currentSets.includes(id));

        // Check if any of the sets being added are archived
        const archivedSetsBeingAdded = setsBeingAdded.filter(setId => {
          const set = sets.find(s => s.id === setId);
          return set?.archived;
        });

        if (archivedSetsBeingAdded.length > 0) {
          throw new Error('Cannot add documents to archived sets');
        }

        // Find sets being removed (currently on document but not in new list)
        const setsBeingRemoved = currentSets.filter(id => !setIds.includes(id));

        // Check if any of the sets being removed are archived
        const archivedSetsBeingRemoved = setsBeingRemoved.filter(setId => {
          const set = sets.find(s => s.id === setId);
          return set?.archived;
        });

        if (archivedSetsBeingRemoved.length > 0) {
          throw new Error('Cannot remove documents from archived sets');
        }
      }

      // Update both documentSets and documentSetOrder
      // Keep the order of existing sets, add new ones at the beginning
      const fileData = fileSnap.data();
      const currentOrder = fileData.documentSetOrder || fileData.documentSets || [];
      
      // Preserve order for sets that are staying, add new ones at beginning
      const existingSetIds = currentOrder.filter(id => setIds.includes(id));
      const newSetIds = setIds.filter(id => !currentOrder.includes(id));
      const finalOrder = [...newSetIds, ...existingSetIds];

      await updateDoc(fileRef, {
        documentSets: setIds,
        documentSetOrder: finalOrder,
      });

      console.log(`Updated document ${fileId} sets to:`, setIds);
    } catch (error) {
      console.error('Error updating document sets:', error);
      throw error;
    }
  },

};

export default DocumentSetService;
